import { useState, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils/cn';
import { QuestionWrapper } from './question-wrapper';
import type { QuestionComponentProps, MaxDiffAnswer, MaxDiffSet } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

interface MaxDiffConfig {
  options?: Array<{ value: string; text: string; order?: number }>;
  itemsPerSet?: number;
  numberOfSets?: number;
  bestLabel?: string;
  worstLabel?: string;
  instructionText?: string;
  displayLayout?: 'table' | 'cards';
  showSetProgress?: boolean;
  showItemNumbers?: boolean;
  highlightSelected?: boolean;
  mobileOptimized?: boolean;
  randomizeSetOrder?: boolean;
  randomizeItemsInSet?: boolean;
}

// ============================================================================
// ICONS
// ============================================================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// ============================================================================
// SET GENERATION
// ============================================================================

function generateBalancedSets(
  optionValues: string[],
  itemsPerSet: number,
  numberOfSets: number,
  randomizeOrder: boolean,
  randomizeItems: boolean
): string[][] {
  const sets: string[][] = [];

  // Track appearances for balance
  const appearances: Record<string, number> = {};
  optionValues.forEach((v) => (appearances[v] = 0));

  for (let i = 0; i < numberOfSets; i++) {
    // Sort by appearances (ascending) and pick least-used items
    const sortedItems = [...optionValues].sort(
      (a, b) => appearances[a] - appearances[b]
    );
    const set = sortedItems.slice(0, itemsPerSet);

    // Optionally randomize items within set
    if (randomizeItems) {
      for (let j = set.length - 1; j > 0; j--) {
        const k = Math.floor(Math.random() * (j + 1));
        [set[j], set[k]] = [set[k], set[j]];
      }
    }

    sets.push(set);
    set.forEach((v) => appearances[v]++);
  }

  // Optionally randomize set order
  if (randomizeOrder) {
    for (let i = sets.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [sets[i], sets[j]] = [sets[j], sets[i]];
    }
  }

  return sets;
}

// ============================================================================
// TABLE LAYOUT COMPONENT
// ============================================================================

interface TableLayoutProps {
  items: string[];
  currentAnswer: MaxDiffSet;
  getOptionText: (value: string) => string;
  onSelect: (type: 'best' | 'worst', value: string) => void;
  bestLabel: string;
  worstLabel: string;
  showItemNumbers: boolean;
  highlightSelected: boolean;
  disabled?: boolean;
  questionCode: string;
  setIndex: number;
}

function TableLayout({
  items,
  currentAnswer,
  getOptionText,
  onSelect,
  bestLabel,
  worstLabel,
  showItemNumbers,
  highlightSelected,
  disabled,
  questionCode,
  setIndex,
}: TableLayoutProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="border-b border-gray-200 dark:border-gray-700 p-3 text-center text-sm font-medium text-green-600 dark:text-green-400">
              {bestLabel}
            </th>
            <th className="border-b border-gray-200 dark:border-gray-700 p-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
              Item
            </th>
            <th className="border-b border-gray-200 dark:border-gray-700 p-3 text-center text-sm font-medium text-red-600 dark:text-red-400">
              {worstLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((optValue, idx) => {
            const isBest = currentAnswer.best === optValue;
            const isWorst = currentAnswer.worst === optValue;

            return (
              <tr
                key={optValue}
                className={cn(
                  'border-b border-gray-100 dark:border-gray-800 transition-colors',
                  highlightSelected && isBest && 'bg-green-50 dark:bg-green-950/30',
                  highlightSelected && isWorst && 'bg-red-50 dark:bg-red-950/30'
                )}
              >
                {/* Best selection */}
                <td className="p-3 text-center">
                  <label className="inline-flex cursor-pointer">
                    <input
                      type="radio"
                      name={`${questionCode}-best-${setIndex}`}
                      checked={isBest}
                      onChange={() => onSelect('best', optValue)}
                      disabled={disabled}
                      className={cn(
                        'h-5 w-5 cursor-pointer',
                        'border-green-400 text-green-600 focus:ring-green-500'
                      )}
                    />
                  </label>
                </td>

                {/* Item text */}
                <td className="p-3 text-sm text-gray-900 dark:text-gray-100">
                  {showItemNumbers && (
                    <span className="mr-2 text-gray-400 dark:text-gray-500">{idx + 1}.</span>
                  )}
                  {getOptionText(optValue)}
                </td>

                {/* Worst selection */}
                <td className="p-3 text-center">
                  <label className="inline-flex cursor-pointer">
                    <input
                      type="radio"
                      name={`${questionCode}-worst-${setIndex}`}
                      checked={isWorst}
                      onChange={() => onSelect('worst', optValue)}
                      disabled={disabled}
                      className={cn(
                        'h-5 w-5 cursor-pointer',
                        'border-red-400 text-red-600 focus:ring-red-500'
                      )}
                    />
                  </label>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// CARDS LAYOUT COMPONENT
// ============================================================================

interface CardsLayoutProps {
  items: string[];
  currentAnswer: MaxDiffSet;
  getOptionText: (value: string) => string;
  onSelect: (type: 'best' | 'worst', value: string) => void;
  bestLabel: string;
  worstLabel: string;
  showItemNumbers: boolean;
  highlightSelected: boolean;
  mobileOptimized: boolean;
  disabled?: boolean;
}

function CardsLayout({
  items,
  currentAnswer,
  getOptionText,
  onSelect,
  bestLabel,
  worstLabel,
  showItemNumbers,
  highlightSelected,
  mobileOptimized,
  disabled,
}: CardsLayoutProps) {
  return (
    <div className={cn('space-y-3', mobileOptimized && 'space-y-2')}>
      {items.map((optValue, idx) => {
        const isBest = currentAnswer.best === optValue;
        const isWorst = currentAnswer.worst === optValue;

        return (
          <div
            key={optValue}
            className={cn(
              'flex items-center gap-3 p-4 border rounded-lg transition-all',
              mobileOptimized && 'p-3 gap-2',
              highlightSelected && isBest && 'border-green-500 bg-green-50 dark:bg-green-950/30 shadow-sm',
              highlightSelected && isWorst && 'border-red-500 bg-red-50 dark:bg-red-950/30 shadow-sm',
              !isBest && !isWorst && 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600',
              disabled && 'opacity-60'
            )}
          >
            {/* Best button */}
            <button
              type="button"
              onClick={() => !disabled && onSelect('best', optValue)}
              disabled={disabled}
              className={cn(
                'flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border-2 transition-all shrink-0',
                mobileOptimized && 'px-2 py-1',
                isBest
                  ? 'bg-green-500 text-white border-green-500'
                  : 'bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-950/30 hover:border-green-400'
              )}
            >
              {isBest && <CheckIcon className="h-3.5 w-3.5" />}
              <span className={mobileOptimized ? 'hidden sm:inline' : ''}>
                {bestLabel}
              </span>
            </button>

            {/* Item text */}
            <div className="flex-1 text-sm text-gray-900 dark:text-gray-100 min-w-0">
              {showItemNumbers && (
                <span className="mr-2 text-gray-400 dark:text-gray-500 font-medium">{idx + 1}.</span>
              )}
              <span className="break-words">{getOptionText(optValue)}</span>
            </div>

            {/* Worst button */}
            <button
              type="button"
              onClick={() => !disabled && onSelect('worst', optValue)}
              disabled={disabled}
              className={cn(
                'flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border-2 transition-all shrink-0',
                mobileOptimized && 'px-2 py-1',
                isWorst
                  ? 'bg-red-500 text-white border-red-500'
                  : 'bg-white dark:bg-gray-800 text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 hover:border-red-400'
              )}
            >
              {isWorst && <XIcon className="h-3.5 w-3.5" />}
              <span className={mobileOptimized ? 'hidden sm:inline' : ''}>
                {worstLabel}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * MaxDiff question component.
 * Presents sets of items and asks respondent to select best and worst from each set.
 * Supports both table and cards layout with various display options.
 */
export function MaxDiff({
  question,
  value,
  onChange,
  disabled,
  errors,
}: QuestionComponentProps<MaxDiffAnswer | null>) {
  const config = question.config as MaxDiffConfig | undefined;

  // Extract options and settings
  const options = config?.options || [];
  const itemsPerSet = config?.itemsPerSet || 4;
  const numberOfSets = config?.numberOfSets || Math.ceil(options.length / 2);

  // Display settings with defaults
  const bestLabel = config?.bestLabel || 'Best';
  const worstLabel = config?.worstLabel || 'Worst';
  const instructionText = config?.instructionText;
  const displayLayout = config?.displayLayout || 'table';
  const showSetProgress = config?.showSetProgress !== false;
  const showItemNumbers = config?.showItemNumbers || false;
  const highlightSelected = config?.highlightSelected !== false;
  const mobileOptimized = config?.mobileOptimized !== false;
  const randomizeSetOrder = config?.randomizeSetOrder !== false;
  const randomizeItemsInSet = config?.randomizeItemsInSet !== false;

  // Generate sets using balanced design
  const sets = useMemo(() => {
    if (options.length === 0) return [];

    const optionValues = options.map((opt) => opt.value);
    return generateBalancedSets(
      optionValues,
      Math.min(itemsPerSet, optionValues.length),
      numberOfSets,
      randomizeSetOrder,
      randomizeItemsInSet
    );
  }, [options, itemsPerSet, numberOfSets, randomizeSetOrder, randomizeItemsInSet]);

  // Current set index
  const [currentSetIndex, setCurrentSetIndex] = useState(0);

  // Initialize answers
  const [answers, setAnswers] = useState<MaxDiffSet[]>(() => {
    if (value?.sets && value.sets.length > 0) {
      return value.sets;
    }
    return sets.map((items, index) => ({
      setIndex: index,
      items,
      best: '',
      worst: '',
    }));
  });

  const currentSet = sets[currentSetIndex] || [];
  const currentAnswer = answers[currentSetIndex] || { setIndex: 0, items: [], best: '', worst: '' };

  const getOptionText = useCallback(
    (optValue: string): string => {
      return options.find((opt) => opt.value === optValue)?.text || optValue;
    },
    [options]
  );

  const handleSelection = useCallback(
    (type: 'best' | 'worst', optValue: string) => {
      if (disabled) return;

      const newAnswers = [...answers];
      const newAnswer = { ...currentAnswer };

      // Can't select same item for both best and worst
      if (type === 'best' && optValue === currentAnswer.worst) {
        newAnswer.worst = '';
      }
      if (type === 'worst' && optValue === currentAnswer.best) {
        newAnswer.best = '';
      }

      newAnswer[type] = optValue;
      newAnswers[currentSetIndex] = newAnswer;
      setAnswers(newAnswers);

      onChange({ sets: newAnswers });
    },
    [disabled, answers, currentAnswer, currentSetIndex, onChange]
  );

  const canGoNext = currentAnswer.best && currentAnswer.worst;
  const isLastSet = currentSetIndex === sets.length - 1;
  const isFirstSet = currentSetIndex === 0;
  const completedSets = answers.filter((a) => a.best && a.worst).length;
  const progressPercent = sets.length > 0 ? (completedSets / sets.length) * 100 : 0;

  // Default instruction based on labels
  const defaultInstruction = `Select the ${bestLabel.toUpperCase()} and ${worstLabel.toUpperCase()} option from the items below:`;

  if (sets.length === 0) {
    return (
      <QuestionWrapper question={question} errors={errors}>
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No items configured for this question.
        </div>
      </QuestionWrapper>
    );
  }

  return (
    <QuestionWrapper question={question} errors={errors}>
      {/* Progress indicator */}
      {showSetProgress && (
        <>
          <div className="mb-3 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <span>
              Set {currentSetIndex + 1} of {sets.length}
            </span>
            <span>
              {completedSets} of {sets.length} completed
            </span>
          </div>

          {/* Progress bar */}
          <div className="mb-5 h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </>
      )}

      {/* Instruction text */}
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        {instructionText || defaultInstruction}
      </p>

      {/* Layout based on display setting */}
      {displayLayout === 'cards' ? (
        <CardsLayout
          items={currentSet}
          currentAnswer={currentAnswer}
          getOptionText={getOptionText}
          onSelect={handleSelection}
          bestLabel={bestLabel}
          worstLabel={worstLabel}
          showItemNumbers={showItemNumbers}
          highlightSelected={highlightSelected}
          mobileOptimized={mobileOptimized}
          disabled={disabled}
        />
      ) : (
        <TableLayout
          items={currentSet}
          currentAnswer={currentAnswer}
          getOptionText={getOptionText}
          onSelect={handleSelection}
          bestLabel={bestLabel}
          worstLabel={worstLabel}
          showItemNumbers={showItemNumbers}
          highlightSelected={highlightSelected}
          disabled={disabled}
          questionCode={question.code}
          setIndex={currentSetIndex}
        />
      )}

      {/* Navigation buttons */}
      <div className="mt-6 flex justify-between">
        <button
          type="button"
          onClick={() => setCurrentSetIndex((prev) => Math.max(0, prev - 1))}
          disabled={isFirstSet || disabled}
          className={cn(
            'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
            isFirstSet
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
          )}
        >
          Previous
        </button>

        {/* Set dots for quick navigation (mobile-friendly) */}
        {mobileOptimized && sets.length <= 10 && (
          <div className="flex items-center gap-1.5">
            {sets.map((_, idx) => {
              const isCompleted = answers[idx]?.best && answers[idx]?.worst;
              const isCurrent = idx === currentSetIndex;

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentSetIndex(idx)}
                  disabled={disabled}
                  className={cn(
                    'w-2.5 h-2.5 rounded-full transition-all',
                    isCurrent
                      ? 'bg-primary-500 scale-125'
                      : isCompleted
                        ? 'bg-green-400 dark:bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                  )}
                  aria-label={`Go to set ${idx + 1}`}
                />
              );
            })}
          </div>
        )}

        {!isLastSet ? (
          <button
            type="button"
            onClick={() =>
              setCurrentSetIndex((prev) => Math.min(sets.length - 1, prev + 1))
            }
            disabled={!canGoNext || disabled}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors',
              !canGoNext
                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-400 dark:text-primary-500 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            )}
          >
            Next
          </button>
        ) : (
          <div className="w-20" /> // Placeholder for layout balance
        )}
      </div>

      {/* Completion status */}
      {isLastSet && canGoNext && (
        <div className="mt-4 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg text-center">
          <p className="text-sm text-green-700 dark:text-green-300 font-medium">
            All sets completed! You can review your answers using the dots above.
          </p>
        </div>
      )}
    </QuestionWrapper>
  );
}
